/**
 * The messages between the page and the import worker (decision 56; part 17 log, table M): the page sends the file
 * picked and its kind, the worker answers with progress, then the book it made (Markdown, DOCX: part 18, table M'), what
 * a bundle holds (part 24b, MB4) or why there is none; or, for a re-import (part 23b, table M″), the page sends a Word
 * file and the worker answers with what part 23a's reader read of it; or, for a bundle's second run (part 24b, MB2), the
 * worker answers with its pieces, a batch at a time, and the page asks for each next one (MB3). Each side reads what it
 * receives field by field (Definition of Done, security), so a message that isn't one of these is never taken for one.
 */
import { ARCHIVE_PROBLEMS, CONVERSION_PROBLEMS, FLAG_KINDS, imageDestination, IMPORT_FORMATS, MAX_AUTHOR, MAX_CHAPTERS, MAX_COMMENT_TEXT, MAX_COMMENTS, MAX_DATE, MAX_PICTURES, MAX_RAW_QUOTE, RAW_CONTEXT, readImageDestination } from '@cockatrice/core';
import type { ArchiveProblem, ConversionProblem, FlagKind, ImportedBook, ImportedChapter, ImportFlag, ImportFormat, ReimportedChapter, ReimportedComment } from '@cockatrice/core';
import { BATCH_BYTES, BATCH_PIECES, BUNDLE_COUNTS, BUNDLE_PROBLEMS, BUNDLE_VERSIONS, pieceBytes, readPiece } from '@cockatrice/export/bundle';
import type { BundleCheck, BundleCounts, BundlePiece, BundleProblem } from '@cockatrice/export/bundle';
import { isId } from '@cockatrice/storage';

/** M1: one file to open as its kind. */
export interface ImportJob {
  readonly kind: 'open';
  readonly format: ImportFormat;
  readonly file: Blob;
}

/** Part 23b, M″1: a Word file to read as this app's export (part 23a). */
export interface ReimportJob {
  readonly kind: 'reimport';
  readonly file: Blob;
}

/** Part 24b, MB2: a bundle's second run: the file, and the first run's SHA-256 of its manifest. */
export interface BundleReadJob {
  readonly kind: 'bundle-read';
  readonly file: Blob;
  readonly manifest: string;
}

/** M1, M″1, MB2: what the page asks of one worker. */
export type WorkerJob = ImportJob | ReimportJob | BundleReadJob;

/** MB3: the page asks for the next batch of a bundle's second run. */
export interface NextBatch {
  readonly kind: 'next';
}

/** M2 (table G): bytes of the file read so far, out of its size. */
export interface ImportProgress {
  readonly kind: 'progress';
  readonly done: number;
  readonly total: number;
}

/** Part 18, G5: a picture the book's text names by its placeholder, to add to the book. */
export interface ImportedFile {
  readonly key: string;
  readonly file: Blob;
}

/** Part 18, M': the book a Markdown or Word file made, and its pictures. */
export interface ImportedReply {
  readonly kind: 'imported';
  readonly book: ImportedBook;
  readonly pictures: readonly ImportedFile[];
}

/**
 * Part 23b, M″2: a picture of a re-imported file: the placeholder its references carry, the image destination the
 * export named it by (null: an editor's, or none), and its bytes typed by their signature (null: the file doesn't hold
 * it, or its part couldn't be read).
 */
export interface ReimportedPictureFile {
  readonly key: string;
  readonly name: string | null;
  readonly file: Blob | null;
}

/**
 * Part 23b, M″2: what part 23a's reader read of a Word file: its chapters, pictures, comments (part 23c) and flags, and
 * the language it names.
 */
export interface ReimportedReply {
  readonly kind: 'reimported';
  readonly chapters: readonly ReimportedChapter[];
  readonly pictures: readonly ReimportedPictureFile[];
  readonly comments: readonly ReimportedComment[];
  readonly flags: readonly ImportFlag[];
  /** The document defaults' language as the file gives it (at most {@link MAX_LANGUAGE} characters), or null. */
  readonly language: string | null;
}

/** M″2: the longest language tag a reply may name (BCP 47's own bound on a tag, 35). */
export const MAX_LANGUAGE = 35;

/** Part 24b, MB4: what a bundle's first run found it holds. */
export interface BundleCheckedReply {
  readonly kind: 'bundle-checked';
  readonly check: BundleCheck;
}

/** Part 24b, MB5: a batch of a bundle's pieces (table O′'s order), and whether it is the last. */
export interface BundleBatch {
  readonly kind: 'bundle-batch';
  readonly pieces: readonly BundlePiece[];
  readonly last: boolean;
}

/**
 * What the worker can say went wrong: the file's problem (table P, P1–P8; part 18's K5, C7, MD5; part 24b's table B), or
 * `failed` (P12).
 */
export type WorkerProblem = ArchiveProblem | ConversionProblem | BundleProblem | 'failed';

/** M4, M', M″2, MB4: the worker's one reply (a bundle's second run ends in its last batch instead). */
export type ImportReply = BundleCheckedReply | ImportedReply | ReimportedReply | { readonly kind: 'refused'; readonly problem: WorkerProblem };

/** M2–M4, MB5: anything the worker sends. */
export type ImportMessage = ImportProgress | ImportReply | BundleBatch;

const PROBLEMS: readonly WorkerProblem[] = [...ARCHIVE_PROBLEMS, ...CONVERSION_PROBLEMS, ...BUNDLE_PROBLEMS, 'failed'];

/** G5: a placeholder destination: a random ID and a zero hash. */
const PLACEHOLDER = /^image:[0-9a-f]{32}\?sha256=0{64}$/u;

/**
 * The own fields of a plain object with exactly `count` fields, or null (as the image worker's, part 16a). Every field
 * a message has is then read by name and checked to be a value of its kind, so the count leaves no room for another.
 */
function fieldsOf(value: unknown, count: number): ReadonlyMap<string, unknown> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const fields = new Map(Object.entries(value));
  return fields.size === count ? fields : null;
}

/** Whether `value` is a whole number from 0. */
function isCount(value: unknown): value is number {
  return Number.isSafeInteger(value) && Number(value) >= 0;
}

/** A string or null. */
function isTitle(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

/** M': a flag: a known kind, a count from 1, and chapter indexes ascending, each under `chapters`. */
function readFlag(value: unknown, chapters: number): ImportFlag | null {
  const fields = fieldsOf(value, 3);
  const kind = FLAG_KINDS.find((known) => known === fields?.get('kind'));
  const count = fields?.get('count');
  const indexes = fields?.get('chapters');
  if (kind === undefined || !isCount(count) || count < 1 || !Array.isArray(indexes)) return null;
  const read: number[] = [];
  for (const index of indexes) {
    if (!isCount(index) || index >= chapters || (read.at(-1) ?? -1) >= index) return null;
    read.push(index);
  }
  return { kind, count, chapters: read };
}

/** M': the book of an `imported` reply, or null. */
function readBook(value: unknown): ImportedBook | null {
  const fields = fieldsOf(value, 4);
  const title = fields?.get('title');
  const subtitle = fields?.get('subtitle');
  const chapters = fields?.get('chapters');
  const flags = fields?.get('flags');
  if (!isTitle(title) || !isTitle(subtitle) || !Array.isArray(chapters)) return null;
  if (chapters.length < 1 || chapters.length > MAX_CHAPTERS) return null;
  const read: ImportedChapter[] = [];
  for (const chapter of chapters) {
    const chapterFields = fieldsOf(chapter, 3);
    const chapterTitle = chapterFields?.get('title');
    const text = chapterFields?.get('text');
    const words = chapterFields?.get('words');
    if (!isTitle(chapterTitle) || typeof text !== 'string' || !isCount(words)) return null;
    read.push({ title: chapterTitle, text, words });
  }
  const readFlagList = readFlags(flags, read.length);
  return readFlagList === null ? null : { title, subtitle, chapters: read, flags: readFlagList };
}

/** M', M″2: a reply's flags, each kind once, each naming chapters under `chapters`; null for anything else. */
function readFlags(value: unknown, chapters: number): ImportFlag[] | null {
  if (!Array.isArray(value)) return null;
  const kinds = new Set<FlagKind>();
  const out: ImportFlag[] = [];
  for (const flag of value) {
    const one = readFlag(flag, chapters);
    if (one === null || kinds.has(one.kind)) return null;
    kinds.add(one.kind);
    out.push(one);
  }
  return out;
}

/** M″2: an ID as part 23a's reader gives one (32 lowercase hex digits), or null. */
function isIdOrNull(value: unknown): value is string | null {
  return value === null || (typeof value === 'string' && /^[0-9a-f]{32}$/u.test(value));
}

/** M″2: the chapters of a `reimported` reply (none to MAX_CHAPTERS), each exactly its five fields; null for anything else. */
function readReimportedChapters(value: unknown): ReimportedChapter[] | null {
  if (!Array.isArray(value) || value.length > MAX_CHAPTERS) return null;
  const out: ReimportedChapter[] = [];
  for (const item of value) {
    const fields = fieldsOf(item, 6);
    const chapter = fields?.get('chapter');
    const snapshot = fields?.get('snapshot');
    const title = fields?.get('title');
    const text = fields?.get('text');
    const words = fields?.get('words');
    const subtitle = fields?.get('subtitle');
    if (!isIdOrNull(chapter) || !isIdOrNull(snapshot) || !isTitle(title) || !isTitle(subtitle) || typeof text !== 'string' || !isCount(words)) return null;
    out.push({ chapter, snapshot, title, subtitle, text, words });
  }
  return out;
}

/**
 * M″2: the pictures of a `reimported` reply (at most MAX_PICTURES), each a placeholder (no key twice), a name that is
 * an image destination as the export writes one, or null, and a Blob or null; null for anything else.
 */
function readReimportedPictures(value: unknown): ReimportedPictureFile[] | null {
  if (!Array.isArray(value) || value.length > MAX_PICTURES) return null;
  const out: ReimportedPictureFile[] = [];
  const keys = new Set<string>();
  for (const item of value) {
    const fields = fieldsOf(item, 3);
    const key = fields?.get('key');
    const name = fields?.get('name');
    const file = fields?.get('file');
    if (typeof key !== 'string' || !PLACEHOLDER.test(key) || keys.has(key)) return null;
    if (name !== null && !(typeof name === 'string' && isDestination(name))) return null;
    if (file !== null && !(file instanceof Blob)) return null;
    keys.add(key);
    out.push({ key, name, file });
  }
  return out;
}

/** M‴: a string of at most `max` units. */
function isText(value: unknown, max: number): value is string {
  return typeof value === 'string' && value.length <= max;
}

/**
 * M‴ (part 23c): the comments of a `reimported` reply (at most MAX_COMMENTS), each exactly its fields: the index of a
 * chapter under `chapters`, a place, the author, date and text within the reader's caps, the quote and context within
 * theirs; null for anything else.
 */
function readReimportedComments(value: unknown, chapters: number): ReimportedComment[] | null {
  if (!Array.isArray(value) || value.length > MAX_COMMENTS) return null;
  const out: ReimportedComment[] = [];
  for (const item of value) {
    const fields = fieldsOf(item, 8);
    const chapter = fields?.get('chapter');
    const at = fields?.get('at');
    const author = fields?.get('author');
    const date = fields?.get('date');
    const text = fields?.get('text');
    const quote = fields?.get('quote');
    const before = fields?.get('before');
    const after = fields?.get('after');
    if (!isCount(chapter) || chapter >= chapters || !isCount(at) || !isText(author, MAX_AUTHOR) || !(date === null || isText(date, MAX_DATE))) return null;
    if (!isText(text, MAX_COMMENT_TEXT) || !isText(quote, MAX_RAW_QUOTE) || !isText(before, RAW_CONTEXT) || !isText(after, RAW_CONTEXT)) return null;
    out.push({ chapter, at, author, date, text, quote, before, after });
  }
  return out;
}

/** M″2: whether `name` is an image destination written as the export writes it. */
function isDestination(name: string): boolean {
  const reference = readImageDestination(name);
  return reference !== null && imageDestination(reference) === name;
}

/** M': the pictures of an `imported` reply, each a placeholder (no key twice) and a Blob; null for anything else. */
function readPictures(value: unknown): ImportedFile[] | null {
  if (!Array.isArray(value) || value.length > MAX_PICTURES) return null;
  const out: ImportedFile[] = [];
  const keys = new Set<string>();
  for (const item of value) {
    const fields = fieldsOf(item, 2);
    const key = fields?.get('key');
    const file = fields?.get('file');
    if (typeof key !== 'string' || !PLACEHOLDER.test(key) || keys.has(key) || !(file instanceof Blob)) return null;
    keys.add(key);
    out.push({ key, file });
  }
  return out;
}

/** MB2, MB4: whether `value` is a SHA-256 as 64 lowercase hex digits. */
function isHash(value: unknown): value is string {
  return typeof value === 'string' && /^[0-9a-f]{64}$/u.test(value);
}

/** M1, M″1, MB2: the job a message asks for, or null for anything else (M5). */
export function readJob(value: unknown): WorkerJob | null {
  const kind: unknown = typeof value === 'object' && value !== null ? Reflect.get(value, 'kind') : undefined;
  if (kind === 'reimport') {
    const file = fieldsOf(value, 2)?.get('file');
    return file instanceof Blob ? { kind, file } : null;
  }
  const fields = fieldsOf(value, 3);
  const file = fields?.get('file');
  if (!(file instanceof Blob)) return null;
  if (kind === 'bundle-read') {
    const manifest = fields?.get('manifest');
    return isHash(manifest) ? { kind, file, manifest } : null;
  }
  const format = IMPORT_FORMATS.find((known) => known === fields?.get('format'));
  return kind === 'open' && format !== undefined ? { kind, format, file } : null;
}

/** MB3: whether a message to a bundle's second run asks for the next batch, exactly. */
export function isNext(value: unknown): boolean {
  return fieldsOf(value, 1)?.get('kind') === 'next';
}

/** MB4: a bundle's counts, exactly MB4's, each a whole number; null for anything else. */
function readCounts(value: unknown): BundleCounts | null {
  const fields = fieldsOf(value, BUNDLE_COUNTS.length);
  if (fields === null) return null;
  const counts = { chapters: 0, trash: 0, copies: 0, snapshots: 0, kept: 0, found: 0, missing: 0, notes: 0, comments: 0, refused: 0, ignored: 0 };
  for (const key of BUNDLE_COUNTS) {
    const count = fields.get(key);
    if (!isCount(count)) return null;
    counts[key] = count;
  }
  return counts;
}

/** MB4: a bundle's first run's reply, or null. */
function readCheck(value: unknown): BundleCheck | null {
  const fields = fieldsOf(value, 5);
  const version = BUNDLE_VERSIONS.find((known) => known === fields?.get('version'));
  const manifest = fields?.get('manifest');
  const book = fields?.get('book');
  const metadata = fields?.get('metadata');
  const counts = readCounts(fields?.get('counts'));
  if (version === undefined || !isHash(manifest) || !isId(book) || !isTitle(metadata) || counts === null) return null;
  return { version, manifest, book, metadata, counts };
}

/** MB5: a batch's pieces: 1 to BATCH_PIECES, each a piece (MB6), one alone or all within BATCH_BYTES (Q); null for anything else. */
function readPieces(value: unknown): BundlePiece[] | null {
  if (!Array.isArray(value) || value.length < 1 || value.length > BATCH_PIECES) return null;
  const pieces: BundlePiece[] = [];
  let bytes = 0;
  for (const item of value) {
    const piece = readPiece(item);
    if (piece === null) return null;
    bytes += pieceBytes(piece);
    pieces.push(piece);
  }
  return pieces.length === 1 || bytes <= BATCH_BYTES ? pieces : null;
}

/** M2–M4, M', MB4, MB5: the message the worker sent, or null for anything else (M5). */
export function readMessage(value: unknown): ImportMessage | null {
  const kind: unknown = typeof value === 'object' && value !== null ? Reflect.get(value, 'kind') : undefined;
  if (kind === 'progress') {
    const fields = fieldsOf(value, 3);
    const done = fields?.get('done');
    const total = fields?.get('total');
    return isCount(done) && isCount(total) && done <= total ? { kind, done, total } : null;
  }
  if (kind === 'bundle-checked') {
    const check = readCheck(fieldsOf(value, 2)?.get('check'));
    return check === null ? null : { kind, check };
  }
  if (kind === 'bundle-batch') {
    const fields = fieldsOf(value, 3);
    const pieces = readPieces(fields?.get('pieces'));
    const last = fields?.get('last');
    return pieces === null || typeof last !== 'boolean' ? null : { kind, pieces, last };
  }
  if (kind === 'imported') {
    const fields = fieldsOf(value, 3);
    const book = readBook(fields?.get('book'));
    const pictures = readPictures(fields?.get('pictures'));
    return book === null || pictures === null ? null : { kind, book, pictures };
  }
  if (kind === 'reimported') {
    const fields = fieldsOf(value, 6);
    const chapters = readReimportedChapters(fields?.get('chapters'));
    const pictures = readReimportedPictures(fields?.get('pictures'));
    const comments = chapters === null ? null : readReimportedComments(fields?.get('comments'), chapters.length);
    const flags = chapters === null ? null : readFlags(fields?.get('flags'), chapters.length);
    const language = fields?.get('language');
    const named = language === null || (typeof language === 'string' && language.length <= MAX_LANGUAGE);
    return chapters === null || pictures === null || comments === null || flags === null || !named ? null : { kind, chapters, pictures, comments, flags, language: typeof language === 'string' ? language : null };
  }
  if (kind === 'refused') {
    const problem = PROBLEMS.find((known) => known === fieldsOf(value, 2)?.get('problem'));
    return problem === undefined ? null : { kind, problem };
  }
  return null;
}
